import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import Header from '../Common/Header';
import BannerSlider from '../Common/BannerSlider';
import BiharElectionMap from './BiharElectionMap';
import BiharElectionNews from '../Public/BiharElectionNews'; // ✅ IMPORT NEWS COMPONENT
import { api } from '../../services/api';
import { signalRService } from '../../services/signalr';
import './LiveResults.css';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const LiveResults = () => {
  const [constituencies, setConstituencies] = useState([]);
  const [selectedConstituency, setSelectedConstituency] = useState(null);
  const [results, setResults] = useState(null);
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [stats, setStats] = useState({
    totalConstituencies: 0,
    totalCandidates: 0,
    resultsDeclared: 0,
    lastUpdated: new Date().toLocaleTimeString()
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState({ 
    connected: false, 
    message: 'Connecting...' 
  });
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [chartType, setChartType] = useState('bar');
  
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    initializeApp();
  }, []);

  const initializeApp = async () => {
    await loadConstituencies();
    await loadStats();
    await initSignalR();
  };

  const initSignalR = async () => {
    try {
      await signalRService.start();
      setConnectionStatus({ connected: true, message: t('results.connected') || 'Connected' });
      signalRService.on('voteUpdate', handleVoteUpdate);
      signalRService.on('resultDeclared', handleResultDeclared);
      signalRService.on('candidateInfo', handleCandidateInfo);
    } catch (error) {
      console.error('SignalR connection error:', error);
      setConnectionStatus({ connected: false, message: t('results.connectionFailed') || 'Connection Failed' });
    }
  };

  const handleVoteUpdate = (update) => {
    addLiveUpdate(
      `🗳️ ${update.candidateName} (${update.partyShortName}) - ${update.votes.toLocaleString()} ${t('results.votes') || 'votes'} ${t('common.in') || 'in'} ${update.constituencyName}`,
      false
    );
    if (selectedConstituency === update.constituencyId) {
      loadResults(update.constituencyId);
    }
    updateLastUpdatedTime();
  };

  const handleResultDeclared = (constituencyId, constituencyName) => {
    addLiveUpdate(`🎉 ${t('results.finalResultDeclared') || 'FINAL RESULT DECLARED'} ${t('common.for') || 'for'} ${constituencyName}!`, true);
    if (selectedConstituency === constituencyId) {
      loadResults(constituencyId);
    }
    loadStats();
  };

  const handleCandidateInfo = (candidate) => {
    addLiveUpdate(`👤 ${t('results.candidateProfileUpdated') || 'Candidate profile updated'}: ${candidate.name}`, false);
  };

  const addLiveUpdate = (message, isFinal) => {
    const newUpdate = {
      id: Date.now(),
      time: new Date().toLocaleTimeString(),
      message,
      isFinal
    };
    setLiveUpdates(prev => [newUpdate, ...prev.slice(0, 49)]);
  };

  const updateLastUpdatedTime = () => {
    setStats(prev => ({ ...prev, lastUpdated: new Date().toLocaleTimeString() }));
  };

  const loadConstituencies = async () => {
    try {
      const data = await api.getConstituencies();
      setConstituencies(data);
      setStats(prev => ({ ...prev, totalConstituencies: data.length }));
    } catch (error) {
      console.error('Error loading constituencies:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.getAllResults();
      let totalCandidates = 0;
      let declaredResults = 0;
      response.forEach(r => {
        totalCandidates += r.candidates?.length || 0;
        if (r.isFinal) declaredResults++;
      });
      setStats(prev => ({
        ...prev,
        totalCandidates,
        resultsDeclared: declaredResults
      }));
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadResults = async (constituencyId) => {
    setSelectedConstituency(constituencyId);
    setLoading(true);
    try {
      const data = await api.getConstituencyResults(constituencyId);
      setResults(data);
      await signalRService.joinConstituency(constituencyId);
    } catch (error) {
      console.error('Error loading results:', error);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const viewCandidateDetails = async (candidateId) => {
    try {
      const candidate = await api.getCandidateById(candidateId);
      setSelectedCandidate(candidate);
      setShowModal(true);
    } catch (error) {
      console.error('Error loading candidate details:', error);
    }
  };

  const filteredCandidates = results?.candidates?.filter(c =>
    c.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.partyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.partyShortName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getChartData = () => {
    if (!results || !results.candidates || results.candidates.length === 0) {
      return null;
    }
    const colors = [
      'rgba(76, 175, 80, 0.8)', 'rgba(255, 153, 51, 0.8)', 'rgba(0, 0, 128, 0.8)',
      'rgba(244, 67, 54, 0.8)', 'rgba(33, 150, 243, 0.8)', 'rgba(156, 39, 176, 0.8)',
      'rgba(255, 152, 0, 0.8)', 'rgba(0, 150, 136, 0.8)'
    ];
    return {
      labels: results.candidates.map(c => c.candidateName),
      datasets: [{
        label: t('results.votes') || 'Votes',
        data: results.candidates.map(c => c.votes),
        backgroundColor: results.candidates.map((c, i) => colors[i % colors.length]),
        borderColor: results.candidates.map((c, i) => colors[i % colors.length].replace('0.8', '1')),
        borderWidth: 2
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: chartType === 'pie', position: 'bottom' },
      title: {
        display: true,
        text: results ? `${results.constituencyName} - ${t('results.voteDistribution') || 'Vote Distribution'}` : t('results.voteDistribution') || 'Vote Distribution',
        font: { size: 18, weight: 'bold' },
        color: '#333'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed.y || context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(2);
            return `${label}: ${value.toLocaleString()} ${t('results.votes') || 'votes'} (${percentage}%)`;
          }
        }
      }
    },
    scales: chartType === 'bar' ? {
      y: {
        beginAtZero: true,
        ticks: { callback: function(value) { return value.toLocaleString(); } }
      }
    } : {}
  };

  const chartData = getChartData();

  return (
    <div className="live-results-page">
      <Header connectionStatus={connectionStatus} />

      {/* Banner Slider */}
      <BannerSlider 
        autoRotate={true}
        rotateInterval={5000}
        showIndicators={true}
      />

      {/* Bihar Election Map Section */}
      <div className="embedded-map-section">
        <BiharElectionMap />
      </div>

      {/* ✅ NEWS SECTION - ADDED UNDER MAP */}
      <div className="news-section-wrapper">
        <BiharElectionNews />
      </div>

      {/* <div className="content-wrapper">

        <div className="stats-grid">
          <StatCard icon="map-marked-alt" title={t('results.totalConstituencies') || 'TOTAL CONSTITUENCIES'} value={stats.totalConstituencies} />
          <StatCard icon="users" title={t('results.totalCandidates') || 'TOTAL CANDIDATES'} value={stats.totalCandidates} />
          <StatCard icon="chart-line" title={t('results.resultsDeclared') || 'RESULTS DECLARED'} value={stats.resultsDeclared} />
          <StatCard icon="clock" title={t('results.lastUpdated') || 'LAST UPDATED'} value={stats.lastUpdated} small />
        </div>


        <div className="filters-container">
          <select
            value={selectedConstituency || ''}
            onChange={(e) => e.target.value && loadResults(parseInt(e.target.value))}
            className="filter-select"
          >
            <option value="">{t('results.selectConstituency') || 'Select a constituency...'}</option>
            {constituencies.map(c => (
              <option key={c.id} value={c.id}>{c.code} - {c.name} ({c.district})</option>
            ))}
          </select>
          <input
            type="text"
            placeholder={t('results.searchPlaceholder') || '🔍 Search candidate or party...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button onClick={() => selectedConstituency && loadResults(selectedConstituency)} className="refresh-btn">
            <i className="fas fa-sync-alt"></i> {t('common.refresh') || 'Refresh'}
          </button>
        </div>


        <div className="main-content-grid">
          <div className="results-section">
            <div className="results-header">
              <h2>{results ? results.constituencyName : (t('results.selectConstituency') || 'Select a constituency')}</h2>
              <div className="badge-container">
                {results && (
                  <>
                    <span className="round-badge">{t('results.round') || 'Round'} {results.roundNo}</span>
                    {results.isFinal && (
                      <span className="final-badge">
                        <i className="fas fa-check-circle"></i> {t('results.final') || 'FINAL'}
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
            <div className="results-content">
              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                  <p>{t('common.loading') || 'Loading results...'}</p>
                </div>
              ) : !results ? (
                <div className="empty-state">
                  <i className="fas fa-inbox"></i>
                  <p>{t('results.selectToView') || 'Select a constituency to view results'}</p>
                </div>
              ) : filteredCandidates && filteredCandidates.length > 0 ? (
                filteredCandidates.map((candidate) => (
                  <CandidateCard key={candidate.candidateId} candidate={candidate} onViewDetails={viewCandidateDetails} t={t} />
                ))
              ) : (
                <div className="empty-state">
                  <i className="fas fa-search"></i>
                  <p>{t('common.noMatch') || 'No candidates match your search'}</p>
                </div>
              )}
            </div>
          </div>

          <div className="live-updates-sidebar">
            <h2>
              <span className="live-indicator"></span>
              {t('results.liveUpdates') || 'Live Updates'}
            </h2>
            <div className="updates-list">
              {liveUpdates.length === 0 ? (
                <div className="update-item">
                  <div className="update-time">{t('common.justNow') || 'Just now'}</div>
                  <div className="update-message">{t('results.waitingForUpdates') || 'Waiting for live updates...'}</div>
                </div>
              ) : (
                liveUpdates.map(update => (
                  <div key={update.id} className={`update-item ${update.isFinal ? 'final' : ''}`}>
                    <div className="update-time">{update.time}</div>
                    <div className="update-message">{update.message}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {chartData && (
          <div className="chart-section">
            <div className="chart-header">
              <h3><i className="fas fa-chart-bar"></i> {t('results.voteDistributionChart') || 'Vote Distribution Chart'}</h3>
              <div className="chart-controls">
                <button className={`chart-btn ${chartType === 'bar' ? 'active' : ''}`} onClick={() => setChartType('bar')}>
                  <i className="fas fa-chart-bar"></i> {t('results.barChart') || 'Bar Chart'}
                </button>
                <button className={`chart-btn ${chartType === 'pie' ? 'active' : ''}`} onClick={() => setChartType('pie')}>
                  <i className="fas fa-chart-pie"></i> {t('results.pieChart') || 'Pie Chart'}
                </button>
              </div>
            </div>
            <div className="chart-container">
              {chartType === 'bar' ? <Bar data={chartData} options={chartOptions} /> : <Pie data={chartData} options={chartOptions} />}
            </div>
          </div>
        )}
      </div> */}

      {showModal && selectedCandidate && (
        <CandidateModal candidate={selectedCandidate} onClose={() => setShowModal(false)} t={t} />
      )}
    </div>
  );
};

const StatCard = ({ icon, title, value, small }) => (
  <div className="stat-card">
    <i className={`fas fa-${icon} stat-icon`}></i>
    <h3>{title}</h3>
    <div className={`value ${small ? 'small' : ''}`}>{value}</div>
  </div>
);

const CandidateCard = ({ candidate, onViewDetails, t }) => (
  <div className={`candidate-card ${candidate.isLeading ? 'leading' : ''} position-${candidate.position}`} onClick={() => onViewDetails(candidate.candidateId)}>
    <div className="candidate-header">
      <div className="candidate-info">
        <h3>
          {candidate.candidateName}
          {candidate.isLeading && <i className="fas fa-crown" style={{ color: 'gold', marginLeft: '10px' }}></i>}
        </h3>
        <p className="party">
          {candidate.partyName}
          <span className="party-badge">{candidate.partyShortName}</span>
        </p>
      </div>
      <div className="position-badge">{candidate.position}</div>
    </div>
    <div className="votes-info">
      <div className="votes">{candidate.votes.toLocaleString()}</div>
      <div className="percentage">{candidate.votePercentage.toFixed(2)}%</div>
    </div>
    <div className="progress-bar">
      <div className="progress-fill" style={{ width: `${candidate.votePercentage}%` }}></div>
    </div>
    <button className="details-btn" onClick={(e) => { e.stopPropagation(); onViewDetails(candidate.candidateId); }}>
      <i className="fas fa-info-circle"></i> {t('results.viewDetails') || 'View Details'}
    </button>
  </div>
);

const CandidateModal = ({ candidate, onClose, t }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <span className="close-btn" onClick={onClose}>&times;</span>
      <h2>{candidate.name}</h2>
      <div className="detail-row">
        <span className="detail-label">{t('results.party') || 'Party'}:</span>
        <span className="detail-value">{candidate.partyName} ({candidate.partyShortName})</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t('results.constituency') || 'Constituency'}:</span>
        <span className="detail-value">{candidate.constituencyName}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t('results.age') || 'Age'}:</span>
        <span className="detail-value">{candidate.age || 'N/A'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t('results.education') || 'Education'}:</span>
        <span className="detail-value">{candidate.education || 'N/A'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t('results.profession') || 'Profession'}:</span>
        <span className="detail-value">{candidate.profession || 'N/A'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t('results.assets') || 'Assets'}:</span>
        <span className="detail-value">₹{candidate.assets ? candidate.assets.toLocaleString() : 'N/A'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t('results.liabilities') || 'Liabilities'}:</span>
        <span className="detail-value">₹{candidate.liabilities ? candidate.liabilities.toLocaleString() : 'N/A'}</span>
      </div>
      <div className="detail-row">
        <span className="detail-label">{t('results.criminalCases') || 'Criminal Cases'}:</span>
        <span className={`detail-value ${candidate.criminalCases > 0 ? 'criminal-cases' : 'no-cases'}`}>
          {candidate.criminalCases || 0}
        </span>
      </div>
      {candidate.backgroundSourceUrl && (
        <div className="detail-row">
          <span className="detail-label">{t('results.source') || 'Source'}:</span>
          <span className="detail-value">
            <a href={candidate.backgroundSourceUrl} target="_blank" rel="noopener noreferrer">
              {t('results.viewFullProfile') || 'View Full Profile'} <i className="fas fa-external-link-alt"></i>
            </a>
          </span>
        </div>
      )}
    </div>
  </div>
);

export default LiveResults;
